import type { ReactNode } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Alert,
  HStack,
  OutlineDangerButton,
  VStack,
} from '@enechain/ecloud-designsystem'

import { Modal, type ModalSize } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { UnsavedChangesDialog } from '~/components/common/UnsavedChangesDialog'
import { useRevealOnChange } from '~/hooks/useRevealOnChange'
import { useUnsavedActionGuard } from '~/hooks/useUnsavedGuard'

type Props = {
  isOpen: boolean
  onClose: () => void
  title: string
  size?: ModalSize
  serverError: string | undefined
  handleSave: () => void
  isPending: boolean
  isSaveDisabled?: boolean
  saveLabel?: string
  cancelLabel?: string
  scrollBehavior?: 'inside' | 'outside'
  /**
   * 未保存の入力があるか (= `useCrudModalForm` の `isDirty` をそのまま渡す)。
   * `true` の間は閉じる操作 (× / キャンセル / Esc / オーバーレイ) を
   * 離脱確認ダイアログで受け止めてから `onClose` する。
   *
   * 省略時 (`undefined`) はガードしない。 デザイナー依頼のガードは代表モーダル
   * から段階適用しており、 未適用のモーダルは従来どおり即座に閉じる。
   */
  isDirty?: boolean
  /**
   * 編集モーダルの削除導線。 指定すると footer の左端に「削除」(破壊的操作) を出し、
   * 押すと呼び出し側が削除確認へ進む。 省略時は出さない (一覧の行アクションから削除するタブ)。
   */
  onDelete?: () => void
  children: ReactNode
}

export const CrudModalLayout = ({
  isOpen,
  onClose,
  title,
  size,
  serverError,
  handleSave,
  isPending,
  isSaveDisabled = false,
  saveLabel,
  cancelLabel,
  scrollBehavior,
  isDirty = false,
  onDelete,
  children,
}: Props): React.ReactElement => {
  const alertRef = useRevealOnChange<HTMLDivElement>(serverError)
  // 閉じる経路は DS `ModalView` が × / Esc / オーバーレイをすべて `onClose` に
  // 集約するため、 `Modal.onClose` と footer のキャンセルの 2 箇所を
  // `guardAction` 経由にすれば全経路がガードされる。
  const { t } = useTranslation()
  const guard = useUnsavedActionGuard({
    isDirty,
    beforeUnload: false,
  })

  const saveCancel = (
    <SaveCancelFooter
      cancelLabel={cancelLabel}
      isPending={isPending}
      isSaveDisabled={isSaveDisabled}
      onCancel={() => guard.guardAction(onClose)}
      onSave={handleSave}
      saveLabel={saveLabel}
    />
  )
  const footer =
    onDelete === undefined ? (
      saveCancel
    ) : (
      <HStack justify="space-between" width="100%">
        <OutlineDangerButton
          disabled={isPending}
          onClick={() => guard.guardAction(onDelete)}
        >
          {t('common:action.delete')}
        </OutlineDangerButton>
        {saveCancel}
      </HStack>
    )

  return (
    <>
      <Modal
        footer={footer}
        isOpen={isOpen}
        onClose={() => guard.guardAction(onClose)}
        scrollBehavior={scrollBehavior}
        size={size}
        title={title}
      >
        <VStack align="stretch" gap={4}>
          {serverError !== undefined && (
            // 欄に付かないエラーは上部に出るので、下までスクロールして保存した時も見える位置へ寄せる
            <div ref={alertRef}>
              <Alert whiteSpace="pre-line" status="error" title={serverError} />
            </div>
          )}
          {children}
        </VStack>
      </Modal>
      <UnsavedChangesDialog
        isOpen={guard.isBlocked}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </>
  )
}
